/**
 * Config Validator
 *
 * Valida la configurazione di un progetto rispetto al Product Catalog.
 * Condiviso tra backend e frontend tramite @scb/shared.
 */
import { SECTION_VARIANTS } from "../catalog/sectionVariants.js";
// ---------------------------------------------------------------------------
// Costanti — sezioni implementate e varianti pianificate
// ---------------------------------------------------------------------------
/**
 * Sezioni implementate — derivate da SECTION_VARIANTS (unica fonte di verità,
 * letta dal codice reale in packages/ui-library/src/sections/*.tsx).
 * Le sezioni non in questo set generano un warning SECTION_NOT_IMPLEMENTED.
 *
 * Prima di questa derivazione era una lista scritta a mano, ferma al "POC
 * v3.95" e mai aggiornata (mancavano ad es. page-hero, search-results,
 * interactive-map — sezioni reali aggiunte dopo).
 */
export const IMPLEMENTED_SECTIONS = new Set(Object.keys(SECTION_VARIANTS));
/**
 * Varianti dichiarate nel catalogo ma non coperte da un ramo di codice
 * distinto — accettate (con warning VARIANT_PLANNED anziché errore
 * INVALID_VARIANT) invece di essere rimosse dal catalogo. Due casi diversi:
 *
 * - vera variante futura, non ancora costruita (es. home-hero/slideshow:
 *   dichiarata nel tipo ImagePosition-like del componente, mai dispatchata);
 * - alias legacy: valore già presente in config reali già deployate (create
 *   dal Builder quando il catalogo aveva ancora `variants[0]` sbagliato),
 *   che non causa un errore di rendering evidente ma non corrisponde a
 *   nessuna variante reale — text-image/default e map-stats-bar/default.
 *   Da migrare e poi rimuovere da qui (vedi report di implementazione).
 */
export const PLANNED_VARIANTS = {
    "home-hero": new Set(["slideshow"]),
    "text-image": new Set(["default"]),
    "map-stats-bar": new Set(["default"]),
};
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Helper: estrae gli ID dei temi (supporta entrambi i formati)
 */
function getThemeIds(themes) {
    if (!themes)
        return [];
    if (Array.isArray(themes)) {
        // Nuovo formato: array di oggetti
        return themes.map((t) => t.id);
    }
    // Vecchio formato: oggetto con chiavi
    return Object.keys(themes);
}
/**
 * Helper: estrae gli ID delle varianti (supporta entrambi i formati)
 */
function getVariantIds(variants) {
    if (!variants)
        return [];
    return variants.map((v) => (typeof v === "string" ? v : v.id));
}
/**
 * Helper: formatta varianti per messaggi di errore
 */
function formatVariants(variants) {
    if (!variants)
        return "";
    return variants.map((v) => (typeof v === "string" ? v : v.id)).join(", ");
}
// ---------------------------------------------------------------------------
// Main validator
// ---------------------------------------------------------------------------
/**
 * Valida l'intera configurazione rispetto al catalogo
 */
export function validateConfig(config, catalogInput, options) {
    const errors = [];
    const warnings = [];
    // Cast to internal CatalogData interface for type-safe access
    const catalog = catalogInput;
    if (!config) {
        errors.push({
            type: "error",
            path: "",
            message: "Configurazione mancante",
            code: "CONFIG_MISSING",
        });
        return { valid: false, errors, warnings };
    }
    // Valida shell
    const shell = config.site?.shell;
    validateShell(shell, catalog, errors, warnings);
    // Valida routing
    validateRouting(config.routing, errors, warnings);
    // Valida pagine
    validatePages(config.pages, catalog, options, errors, warnings);
    // Valida dataSources
    validateDataSources(config.dataSources, errors, warnings);
    // Valida i riferimenti incrociati (dataSourceId, notFoundPageId, link interni)
    // — nessuno di questi era controllato prima di questa validazione: un
    // build-config poteva riferire un dataSourceId inesistente (es. il nome di
    // un adapter invece dell'id di un datasource configurato) e l'upload
    // veniva accettato comunque, rompendo la ricerca solo a runtime.
    validateCrossReferences(config, errors, warnings);
    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}
// ---------------------------------------------------------------------------
// Cross-reference validation — dataSourceId, notFoundPageId, link interni
// ---------------------------------------------------------------------------
function validateCrossReferences(config, errors, warnings) {
    const pages = Array.isArray(config.pages)
        ? config.pages
        : [];
    const dataSources = Array.isArray(config.dataSources)
        ? config.dataSources
        : [];
    const dataSourceIds = new Set(dataSources.map((ds) => ds.id).filter(Boolean));
    // --- dataSourceId: props di sezione (binding kind:"literal") + page.dataAccess.sourceId ---
    pages.forEach((page, pageIndex) => {
        const basePath = `pages[${pageIndex}]`;
        const dataAccess = page.dataAccess;
        const sourceId = dataAccess?.sourceId;
        if (sourceId && !dataSourceIds.has(sourceId)) {
            errors.push({
                type: "error",
                path: `${basePath}.dataAccess.sourceId`,
                message: `dataSource "${sourceId}" non presente in dataSources[] (dataAccess.sourceId di ${page.id ?? "?"})`,
                code: "DATASOURCE_REF_MISSING",
            });
        }
        const sections = Array.isArray(page.sections)
            ? page.sections
            : [];
        sections.forEach((section, sectionIndex) => {
            const props = section.props;
            const binding = props?.dataSourceId;
            if (binding &&
                typeof binding === "object" &&
                binding.kind === "literal") {
                const value = binding.value;
                if (value && !dataSourceIds.has(value)) {
                    errors.push({
                        type: "error",
                        path: `${basePath}.sections[${sectionIndex}].props.dataSourceId`,
                        message: `dataSource "${value}" non presente in dataSources[] (sezione "${section.sectionId ?? "?"}")`,
                        code: "DATASOURCE_REF_MISSING",
                    });
                }
            }
        });
    });
    // --- routing.notFoundPageId ---
    const routing = config.routing;
    const notFoundPageId = routing?.notFoundPageId;
    if (notFoundPageId) {
        const exists = pages.some((p) => p.id === notFoundPageId);
        if (!exists) {
            // Warning e non errore: il frontend ha un 404 di fallback, quindi il
            // riferimento morto non rompe nulla di visibile. Bloccano solo i
            // difetti che degradano male, come un dataSourceId inesistente.
            warnings.push({
                type: "warning",
                path: "routing.notFoundPageId",
                message: `notFoundPageId "${notFoundPageId}" non corrisponde a nessuna pagina`,
                code: "NOTFOUND_PAGE_MISSING",
            });
        }
    }
    // --- link interni morti (nav + footer) ---
    const knownPaths = new Set(pages
        .map((p) => p.route?.pathPattern)
        .filter(Boolean));
    const redirects = Array.isArray(routing?.redirects)
        ? routing.redirects
        : [];
    redirects.forEach((r) => {
        if (typeof r.from === "string")
            knownPaths.add(r.from);
    });
    function checkInternalLink(href, path) {
        if (typeof href !== "string" || !href.startsWith("/"))
            return;
        const pathname = href.split(/[?#]/)[0];
        const matches = [...knownPaths].some((pattern) => pathPatternMatches(pattern, pathname));
        if (!matches) {
            warnings.push({
                type: "warning",
                path,
                message: `Link interno "${href}" non corrisponde a nessuna route né a un redirect`,
                code: "INTERNAL_LINK_DEAD",
            });
        }
    }
    const shell = config.site?.shell;
    const nav = shell?.nav;
    const navItems = nav?.items ?? [];
    navItems.forEach((item, index) => {
        checkInternalLink(item.href, `site.shell.nav.items[${index}].href`);
    });
    const ctaButton = nav?.ctaButton;
    if (ctaButton) {
        checkInternalLink(ctaButton.href, "site.shell.nav.ctaButton.href");
    }
    const footer = shell?.footer;
    const columns = footer?.columns ?? [];
    columns.forEach((column, columnIndex) => {
        const links = column.links ?? [];
        links.forEach((link, linkIndex) => {
            checkInternalLink(link.href, `site.shell.footer.columns[${columnIndex}].links[${linkIndex}].href`);
        });
    });
    const bottomLinks = footer?.bottomLinks ?? [];
    bottomLinks.forEach((link, index) => {
        checkInternalLink(link.href, `site.shell.footer.bottomLinks[${index}].href`);
    });
}
/**
 * Confronta un href statico con un pathPattern che può contenere segmenti
 * dinamici (es. "/works/:id"). Un link di nav/footer punta quasi sempre a un
 * path statico, ma i pathPattern delle pagine "entity-driven" non lo sono.
 */
function pathPatternMatches(pattern, pathname) {
    if (pattern === pathname)
        return true;
    const patternSegments = pattern.split("/").filter(Boolean);
    const pathSegments = pathname.split("/").filter(Boolean);
    if (patternSegments.length !== pathSegments.length)
        return false;
    return patternSegments.every((seg, i) => seg.startsWith(":") || seg === pathSegments[i]);
}
// ---------------------------------------------------------------------------
// Shell validation
// ---------------------------------------------------------------------------
function validateShell(shell, catalog, errors, warnings) {
    const basePath = "site.shell";
    if (!shell) {
        warnings.push({
            type: "warning",
            path: basePath,
            message: "Shell non configurata, verranno usati i valori di default",
            code: "SHELL_MISSING",
        });
        return;
    }
    const shellObj = shell;
    // Valida tema
    const themeIds = getThemeIds(catalog.themes);
    if (shellObj.theme &&
        themeIds.length > 0 &&
        !themeIds.includes(shellObj.theme)) {
        errors.push({
            type: "error",
            path: `${basePath}.theme`,
            message: `Tema "${shellObj.theme}" non presente nel catalogo. Temi disponibili: ${themeIds.join(", ")}`,
            code: "INVALID_THEME",
        });
    }
    // Valida brand
    const brand = shellObj.brand;
    if (!brand?.title) {
        warnings.push({
            type: "warning",
            path: `${basePath}.brand.title`,
            message: "Nome del sito non configurato",
            code: "BRAND_TITLE_MISSING",
        });
    }
    // Valida nav items
    const nav = shellObj.nav;
    const navItems = nav?.items;
    if (navItems) {
        navItems.forEach((item, index) => {
            if (!item.label) {
                errors.push({
                    type: "error",
                    path: `${basePath}.nav.items[${index}].label`,
                    message: "Label voce menu mancante",
                    code: "NAV_ITEM_LABEL_MISSING",
                });
            }
            if (!item.href) {
                errors.push({
                    type: "error",
                    path: `${basePath}.nav.items[${index}].href`,
                    message: "URL voce menu mancante",
                    code: "NAV_ITEM_HREF_MISSING",
                });
            }
        });
    }
    // Valida footer
    const footer = shellObj.footer;
    const contact = footer?.contact;
    if (contact?.email && !isValidEmail(contact.email)) {
        errors.push({
            type: "error",
            path: `${basePath}.footer.contact.email`,
            message: "Email non valida",
            code: "INVALID_EMAIL",
        });
    }
    // Valida social links
    const social = footer?.social;
    if (social) {
        social.forEach((link, index) => {
            if (!link.platform) {
                errors.push({
                    type: "error",
                    path: `${basePath}.footer.social[${index}].platform`,
                    message: "Piattaforma social mancante",
                    code: "SOCIAL_PLATFORM_MISSING",
                });
            }
            if (!link.url) {
                errors.push({
                    type: "error",
                    path: `${basePath}.footer.social[${index}].url`,
                    message: "URL social mancante",
                    code: "SOCIAL_URL_MISSING",
                });
            }
            else if (!isValidUrl(link.url)) {
                errors.push({
                    type: "error",
                    path: `${basePath}.footer.social[${index}].url`,
                    message: "URL social non valido",
                    code: "INVALID_SOCIAL_URL",
                });
            }
        });
    }
}
// ---------------------------------------------------------------------------
// Routing validation
// ---------------------------------------------------------------------------
function validateRouting(routing, errors, _warnings) {
    if (!routing)
        return;
    const routingObj = routing;
    const redirects = routingObj.redirects;
    if (redirects) {
        redirects.forEach((redirect, index) => {
            if (!redirect.from) {
                errors.push({
                    type: "error",
                    path: `routing.redirects[${index}].from`,
                    message: "Path sorgente redirect mancante",
                    code: "REDIRECT_FROM_MISSING",
                });
            }
            if (!redirect.to) {
                errors.push({
                    type: "error",
                    path: `routing.redirects[${index}].to`,
                    message: "Path destinazione redirect mancante",
                    code: "REDIRECT_TO_MISSING",
                });
            }
        });
    }
}
// ---------------------------------------------------------------------------
// Pages validation
// ---------------------------------------------------------------------------
function validatePages(pages, catalog, options, errors, warnings) {
    if (!pages || !Array.isArray(pages)) {
        warnings.push({
            type: "warning",
            path: "pages",
            message: "Nessuna pagina configurata",
            code: "NO_PAGES",
        });
        return;
    }
    // Explicit re-assertion after Array.isArray narrowing (TS 5.5+ narrows to unknown[])
    const typedPages = pages;
    // Verifica homepage
    const hasHomepage = typedPages.some((p) => {
        const route = p.route;
        return route?.pathPattern === "/" && p.enabled;
    });
    if (!hasHomepage) {
        warnings.push({
            type: "warning",
            path: "pages",
            message: 'Nessuna homepage configurata (path "/")',
            code: "NO_HOMEPAGE",
        });
    }
    // Verifica ID duplicati
    const pageIds = typedPages.map((p) => p.id);
    const duplicateIds = pageIds.filter((id, index) => pageIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
        errors.push({
            type: "error",
            path: "pages",
            message: `ID pagina duplicati: ${[...new Set(duplicateIds)].join(", ")}`,
            code: "DUPLICATE_PAGE_ID",
        });
    }
    // Verifica path duplicati
    const pagePaths = typedPages
        .filter((p) => p.enabled)
        .map((p) => p.route?.pathPattern);
    const duplicatePaths = pagePaths.filter((path, index) => path && pagePaths.indexOf(path) !== index);
    if (duplicatePaths.length > 0) {
        errors.push({
            type: "error",
            path: "pages",
            message: `Path duplicati: ${[...new Set(duplicatePaths)].join(", ")}`,
            code: "DUPLICATE_PAGE_PATH",
        });
    }
    // Valida ogni pagina
    typedPages.forEach((page, index) => {
        validatePage(page, index, catalog, options, errors, warnings);
    });
}
function validatePage(page, index, catalog, options, errors, warnings) {
    const basePath = `pages[${index}]`;
    const route = page.route;
    // ID obbligatorio
    if (!page.id) {
        errors.push({
            type: "error",
            path: `${basePath}.id`,
            message: "ID pagina mancante",
            code: "PAGE_ID_MISSING",
        });
    }
    // Template valido
    if (!page.templateId) {
        errors.push({
            type: "error",
            path: `${basePath}.templateId`,
            message: "Template non specificato",
            code: "TEMPLATE_MISSING",
        });
    }
    else if (catalog.templates &&
        !catalog.templates[page.templateId]) {
        errors.push({
            type: "error",
            path: `${basePath}.templateId`,
            message: `Template "${page.templateId}" non presente nel catalogo. Template disponibili: ${Object.keys(catalog.templates).join(", ")}`,
            code: "INVALID_TEMPLATE",
        });
    }
    // Route obbligatoria
    if (!route?.pathPattern) {
        errors.push({
            type: "error",
            path: `${basePath}.route.pathPattern`,
            message: "Path URL mancante",
            code: "PAGE_PATH_MISSING",
        });
    }
    // PageKind valido
    if (route?.pageKind) {
        if (catalog.allowedPageKinds &&
            !catalog.allowedPageKinds.includes(route.pageKind)) {
            errors.push({
                type: "error",
                path: `${basePath}.route.pageKind`,
                message: `PageKind "${route.pageKind}" non valido. Valori ammessi: ${catalog.allowedPageKinds.join(", ")}`,
                code: "INVALID_PAGE_KIND",
            });
        }
    }
    // Label consigliata
    if (!page.label && page.enabled) {
        warnings.push({
            type: "warning",
            path: `${basePath}.label`,
            message: "Label pagina non specificata",
            code: "PAGE_LABEL_MISSING",
        });
    }
    // Layout variant valida (se specificata)
    if (page.layoutVariant && page.templateId) {
        const template = catalog.templates?.[page.templateId];
        if (template?.layoutVariants) {
            const validVariantIds = template.layoutVariants.map((v) => v.id);
            if (!validVariantIds.includes(page.layoutVariant)) {
                errors.push({
                    type: "error",
                    path: `${basePath}.layoutVariant`,
                    message: `Layout variant "${page.layoutVariant}" non valida per template "${page.templateId}". Varianti disponibili: ${validVariantIds.join(", ")}`,
                    code: "INVALID_LAYOUT_VARIANT",
                });
            }
        }
        else {
            warnings.push({
                type: "warning",
                path: `${basePath}.layoutVariant`,
                message: `Template "${page.templateId}" non supporta layout variants`,
                code: "LAYOUT_VARIANT_NOT_SUPPORTED",
            });
        }
    }
    // Valida sezioni
    const sections = page.sections;
    if (sections && Array.isArray(sections)) {
        const template = catalog.templates?.[page.templateId];
        const allowedSlots = template?.slots || [];
        sections.forEach((section, sectionIndex) => {
            validateSection(section, sectionIndex, basePath, catalog, allowedSlots, options, errors, warnings);
        });
    }
}
// ---------------------------------------------------------------------------
// Section validation
// ---------------------------------------------------------------------------
function validateSection(section, index, parentPath, catalog, allowedSlots, options, errors, warnings) {
    const basePath = `${parentPath}.sections[${index}]`;
    // ID obbligatorio
    if (!section.id) {
        errors.push({
            type: "error",
            path: `${basePath}.id`,
            message: "ID sezione mancante",
            code: "SECTION_ID_MISSING",
        });
    }
    // SectionId valido
    if (!section.sectionId) {
        errors.push({
            type: "error",
            path: `${basePath}.sectionId`,
            message: "Tipo sezione non specificato",
            code: "SECTION_TYPE_MISSING",
        });
    }
    else if (catalog.sections &&
        !catalog.sections[section.sectionId]) {
        errors.push({
            type: "error",
            path: `${basePath}.sectionId`,
            message: `Sezione "${section.sectionId}" non presente nel catalogo`,
            code: "INVALID_SECTION_TYPE",
        });
    }
    else {
        // Warning se la sezione non e ancora implementata (solo se implementedSections fornito)
        const sectionId = section.sectionId;
        if (options?.implementedSections &&
            !options.implementedSections.has(sectionId)) {
            warnings.push({
                type: "warning",
                path: `${basePath}.sectionId`,
                message: `Sezione "${sectionId}" non ancora implementata nel POC (verra mostrato un placeholder)`,
                code: "SECTION_NOT_IMPLEMENTED",
            });
        }
    }
    // Slot valido per il template
    if (section.slot) {
        if (allowedSlots.length > 0 &&
            !allowedSlots.includes(section.slot)) {
            errors.push({
                type: "error",
                path: `${basePath}.slot`,
                message: `Slot "${section.slot}" non valido per questo template. Slot disponibili: ${allowedSlots.join(", ")}`,
                code: "INVALID_SLOT",
            });
        }
        // Verifica che la sezione sia ammessa nello slot
        const sectionDef = catalog.sections?.[section.sectionId];
        if (sectionDef?.allowedSlots &&
            !sectionDef.allowedSlots.includes(section.slot)) {
            errors.push({
                type: "error",
                path: `${basePath}.slot`,
                message: `La sezione "${section.sectionId}" non puo essere inserita nello slot "${section.slot}". Slot ammessi: ${sectionDef.allowedSlots.join(", ")}`,
                code: "SECTION_SLOT_MISMATCH",
            });
        }
    }
    // Variante valida
    if (section.variant && section.sectionId) {
        const sectionDef = catalog.sections?.[section.sectionId];
        const variantIds = getVariantIds(sectionDef?.variants);
        if (variantIds.length > 0 &&
            !variantIds.includes(section.variant)) {
            errors.push({
                type: "error",
                path: `${basePath}.variant`,
                message: `Variante "${section.variant}" non valida per "${section.sectionId}". Varianti disponibili: ${formatVariants(sectionDef?.variants)}`,
                code: "INVALID_VARIANT",
            });
        }
        else {
            // Warning se la variante e "planned" (solo se plannedVariants fornito)
            const sectionId = section.sectionId;
            const variantId = section.variant;
            if (options?.plannedVariants?.[sectionId]?.has(variantId)) {
                warnings.push({
                    type: "warning",
                    path: `${basePath}.variant`,
                    message: `Variante "${variantId}" per "${sectionId}" e in sviluppo (coming soon)`,
                    code: "VARIANT_PLANNED",
                });
            }
        }
    }
    // Valida props obbligatorie
    validateSectionProps(section.sectionId, section.props, basePath, errors, warnings);
}
// ---------------------------------------------------------------------------
// Props validation
// ---------------------------------------------------------------------------
/**
 * Valida props di una sezione
 *
 * NOTA: I nomi delle props possono variare tra configurazioni (es. "text" vs "body", "image" vs "imageUrl")
 * Quindi invece di controllare nomi specifici, verifichiamo che i binding siano ben formati.
 */
function validateSectionProps(_sectionId, props, parentPath, errors, warnings) {
    const basePath = `${parentPath}.props`;
    if (!props) {
        // Props mancanti - potrebbe essere ok se la sezione non ne richiede
        return;
    }
    // Verifica che ogni prop sia un binding valido
    for (const [propName, propValue] of Object.entries(props)) {
        if (propValue === null || propValue === undefined)
            continue;
        if (typeof propValue === "object" &&
            "kind" in propValue) {
            const binding = propValue;
            const kind = binding.kind;
            // Verifica struttura binding
            if (kind === "literal") {
                // literal con value undefined/null e warning, non errore
                // (potrebbe essere intenzionalmente vuoto)
            }
            else if (kind === "fromDto" || kind === "fromRoute") {
                // fromDto e fromRoute devono avere un path
                if (!binding.path) {
                    warnings.push({
                        type: "warning",
                        path: `${basePath}.${propName}`,
                        message: `Binding "${propName}" (${kind}): path non specificato`,
                        code: "BINDING_PATH_MISSING",
                    });
                }
            }
            else if (kind !== undefined) {
                errors.push({
                    type: "error",
                    path: `${basePath}.${propName}`,
                    message: `Tipo binding "${kind}" non riconosciuto. Usa: literal, fromDto, fromRoute`,
                    code: "INVALID_BINDING_KIND",
                });
            }
        }
        // Se non e un binding object, e un valore diretto (legacy o semplificato) - va bene
    }
}
// ---------------------------------------------------------------------------
// DataSources validation
// ---------------------------------------------------------------------------
function validateDataSources(dataSources, errors, _warnings) {
    if (!dataSources || !Array.isArray(dataSources)) {
        return;
    }
    // Explicit re-assertion after Array.isArray narrowing (TS 5.5+ narrows to unknown[])
    const typedDS = dataSources;
    // Verifica ID duplicati
    const ids = typedDS.map((ds) => ds.id);
    const duplicateIds = ids.filter((id, index) => id && ids.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
        errors.push({
            type: "error",
            path: "dataSources",
            message: `ID dataSource duplicati: ${[...new Set(duplicateIds)].join(", ")}`,
            code: "DUPLICATE_DATASOURCE_ID",
        });
    }
    typedDS.forEach((ds, index) => {
        const basePath = `dataSources[${index}]`;
        if (!ds.id) {
            errors.push({
                type: "error",
                path: `${basePath}.id`,
                message: "ID dataSource mancante",
                code: "DATASOURCE_ID_MISSING",
            });
        }
        if (!ds.adapter) {
            errors.push({
                type: "error",
                path: `${basePath}.adapter`,
                message: "Adapter dataSource mancante",
                code: "DATASOURCE_ADAPTER_MISSING",
            });
        }
    });
}
// ---------------------------------------------------------------------------
// Utility functions — risultati per path (usate dal frontend)
// ---------------------------------------------------------------------------
/**
 * Conta errori per path (per mostrare badge)
 */
export function countErrorsByPath(result, pathPrefix) {
    return {
        errors: result.errors.filter((e) => e.path.startsWith(pathPrefix)).length,
        warnings: result.warnings.filter((w) => w.path.startsWith(pathPrefix))
            .length,
    };
}
/**
 * Filtra messaggi per path
 */
export function getMessagesForPath(result, pathPrefix) {
    return [
        ...result.errors.filter((e) => e.path.startsWith(pathPrefix)),
        ...result.warnings.filter((w) => w.path.startsWith(pathPrefix)),
    ];
}
// ---------------------------------------------------------------------------
// Internal utilities
// ---------------------------------------------------------------------------
function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=configValidator.js.map